import { cloneElement, useEffect, useState, type ReactElement } from 'react';
import type { UserListFilterKind } from '@backstage/plugin-catalog-react';

// The catalog, API Docs and TechDocs pages all render the same
// `UserListPicker` from @backstage/plugin-catalog-react. We persist its
// selection in a single shared localStorage entry so a user's "Starred"
// (or whichever) preference follows them across all three pages.
//
// How each page reads it back on mount (all three are new-frontend-system
// pages of the packages' `/alpha` plugins):
//   - `/catalog` (PAT-1027): `modules/catalog/catalogIndexPage.tsx` overrides
//     the `catalog-filter:catalog/list` extension, so the `UserListPicker`
//     is wrapped in `PersistedUserListFilterPage` (`propName="initialFilter"`).
//   - `/docs` (PAT-1024): the `page:techdocs` override in `modules/techdocs/`
//     wraps the index content in `PersistedUserListFilterPage`.
//   - `/api-docs` (PAT-1022): `initiallySelectedFilter` is static extension
//     config, so `modules/api-docs/apiExplorerPage.tsx` builds one page
//     element per filter and lets `PersistedUserListFilterElement` pick one
//     on mount.
//
// URL-wins precedence (deliberate, do NOT fight it):
// `EntityListProvider` reads `?filters[user]=...` from the URL and only
// falls back to `initial(ly)Filter` when the URL has no such param. That
// means in these flows the persisted value is *intentionally* ignored:
//   - browser Back restoring `/catalog?filters[user]=owned`
//   - hard refresh of a URL that already has `filters[user]`
//   - pasted / bookmarked / shared deep links
// Sidebar nav and fresh tabs land on a clean URL, so localStorage wins.
//
// We could force localStorage to always win by stripping
// `filters[user]` from `window.location` via `history.replaceState` in
// the wrapper below before EntityListProvider reads it — but that breaks
// deep links, bookmarks, share-links and browser-back state, so we
// deliberately don't. If a future ticket asks for "persistence must
// always win", that's the one-line lever to pull (see
// PersistedUserListFilterPage).
const STORAGE_KEY = 'backstage.userListFilter';
const TESTID_PREFIX = 'user-picker-';

/** Every value `UserListPicker` can select, in its display order. */
export const USER_LIST_FILTERS = [
  'owned',
  'starred',
  'all',
] as const satisfies readonly UserListFilterKind[];

const isFilter = (v: unknown): v is UserListFilterKind =>
  (USER_LIST_FILTERS as readonly unknown[]).includes(v);

/** Reads the persisted user-list filter. Falls back to `defaultFilter`
 * if nothing is stored, the stored value is unrecognised, or
 * localStorage is unavailable (e.g. blocked by browser settings). */
export const readStoredUserListFilter = (
  defaultFilter: UserListFilterKind,
): UserListFilterKind => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isFilter(v) ? v : defaultFilter;
  } catch {
    return defaultFilter;
  }
};

/** Single document-level click listener that mirrors the user's Owned /
 * Starred / All picker selection into localStorage. Mount once at app
 * root — the same listener covers catalog, api-docs and techdocs since
 * they all render the same `UserListPicker`.
 *
 * Why capture-phase click on `document` (not a React onClick / not URL
 * sync):
 *   1. `UserListPicker` auto-falls back to "all" via a state setter
 *      whenever the chosen filter has 0 matching entities. That
 *      programmatic change writes the URL (`replaceState`) but does NOT
 *      dispatch a DOM click — so listening on real clicks means our
 *      storage only ever reflects what the user explicitly chose.
 *   2. Capture phase runs before any bubble-phase `stopPropagation` in
 *      MUI's `MenuItem` could hide the event from us.
 *   3. One listener at the document covers all three pages without each
 *      page needing its own wiring.
 */
export const UserListFilterPersister = () => {
  useEffect(() => {
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement | null;
      const picker = target?.closest?.(`[data-testid^="${TESTID_PREFIX}"]`);
      // `aria-disabled` (not native `disabled`) because MUI MenuItem
      // renders as <li>, where the browser still fires `click` events
      // even when MUI considers it disabled.
      if (!picker || picker.getAttribute('aria-disabled') === 'true') return;
      const value = (picker.getAttribute('data-testid') ?? '').slice(
        TESTID_PREFIX.length,
      );
      if (!isFilter(value)) return;
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch {
        /* localStorage unavailable — ignore */
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
  return null;
};

/** Wraps the element that takes the initial filter so the persisted
 * filter is freshly injected on every mount: the `UserListPicker` in the
 * `catalog-filter:catalog/list` override of `modules/catalog` (PAT-1027)
 * and `TechDocsIndexContent` in the `page:techdocs` loader of
 * `modules/techdocs` (PAT-1024). (`/api-docs` uses
 * `PersistedUserListFilterElement` instead.)
 *
 * Reading localStorage inside a `useState` initializer (rather than at
 * module load) is essential: every navigation back to the route
 * remounts this wrapper and re-reads the stored preference.
 *
 * Note on prop naming: both current callers pass `initialFilter`; the
 * legacy `CatalogIndexPage` took `initiallySelectedFilter` (yes, the
 * upstream names differ — that is why this prop is parameterised).
 *
 * If you ever need "localStorage always wins, even over the URL" (see
 * the URL-wins precedence note at the top of this file), the cheapest
 * way is to strip `filters[user]` from the URL here before the inner
 * page mounts, e.g. inside the `useState` initializer:
 *   const u = new URL(window.location.href);
 *   if (u.searchParams.has('filters[user]')) {
 *     u.searchParams.delete('filters[user]');
 *     window.history.replaceState(null, '', u.toString());
 *   }
 * Be aware that this breaks deep links, bookmarks and browser-back
 * restoration — only add it if that trade-off is explicitly wanted.
 */
export const PersistedUserListFilterPage = ({
  propName,
  defaultFilter,
  children,
}: {
  /** `UserListPicker` and TechDocs accept `initialFilter`; the legacy
   * `CatalogIndexPage` accepted `initiallySelectedFilter`. */
  propName: 'initiallySelectedFilter' | 'initialFilter';
  /** Filter to use when nothing valid is stored. */
  defaultFilter: UserListFilterKind;
  children: ReactElement;
}) => {
  const [initial] = useState<UserListFilterKind>(() =>
    readStoredUserListFilter(defaultFilter),
  );
  return cloneElement(children, { [propName]: initial });
};

/** Picks, once per mount, the element matching the persisted filter.
 *
 * For new-frontend-system page overrides, where the filter is a static
 * extension config value rather than a JSX prop: the override builds one
 * page element per filter at factory time (`USER_LIST_FILTERS`) and
 * renders this component in their place. The `useState` initializer
 * re-reads localStorage on every mount, as `PersistedUserListFilterPage`
 * does; while mounted the element never changes, so a click in the picker
 * (which the persister writes to storage) does not remount the page.
 * URL-wins precedence is unchanged: the chosen page still prefers
 * `?filters[user]=` over its initial filter. */
export const PersistedUserListFilterElement = ({
  elements,
  defaultFilter,
}: {
  /** One element per filter value. */
  elements: Record<UserListFilterKind, ReactElement>;
  /** Filter to use when nothing valid is stored. */
  defaultFilter: UserListFilterKind;
}) => {
  const [initial] = useState<UserListFilterKind>(() =>
    readStoredUserListFilter(defaultFilter),
  );
  return elements[initial];
};

// Exported only for tests so they can assert against the key without
// hard-coding it in multiple places.
export const __STORAGE_KEY = STORAGE_KEY;
